import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit'
import ApiClient from '../../services/apiClient/ApiClient'
import { getBuildingTagsWithBuildingAssociations } from '../../pages/Explore/DeveloperMap/services/buildingTagsService'
import type {
  TagWithUserInfo,
  TagWithBuildings,
  BuildingTagsState,
  CreateUserBuildingTagDto,
} from '@popety-io/types'

const apiClient = new ApiClient()

// Async thunk for getting all building tags
export const getAllBuildingTags = createAsyncThunk(
  'buildingTags/getAllBuildingTags',
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiClient.get('/tags/building/all')
      if (response.errors) {
        return rejectWithValue(response.errors[0])
      }
      return response.data as TagWithUserInfo[]
    } catch {
      return rejectWithValue({ message: 'Failed to fetch building tags' })
    }
  }
)

// Async thunk for creating a building tag
export const createBuildingTag = createAsyncThunk(
  'buildingTags/createBuildingTag',
  async (tagData: CreateUserBuildingTagDto, { rejectWithValue }) => {
    try {
      const response = await apiClient.post('/tags/building', tagData)
      if (response.errors) {
        return rejectWithValue(response.errors[0])
      }
      return response.data as TagWithUserInfo
    } catch {
      return rejectWithValue({ message: 'Failed to create building tag' })
    }
  }
)

// Async thunk for deleting a building tag
export const deleteBuildingTag = createAsyncThunk(
  'buildingTags/deleteBuildingTag',
  async (tagId: string, { rejectWithValue }) => {
    try {
      const response = await apiClient.delete(`/tags/building/${tagId}`)
      if (response.errors) {
        return rejectWithValue(response.errors[0])
      }
      return tagId
    } catch {
      return rejectWithValue({ message: 'Failed to delete building tag' })
    }
  }
)

// Async thunk for getting building tags with building associations
export const getBuildingTagsWithBuildings = createAsyncThunk(
  'buildingTags/getBuildingTagsWithBuildings',
  async (_, { rejectWithValue }) => {
    try {
      const tagsWithBuildings = await getBuildingTagsWithBuildingAssociations()
      return tagsWithBuildings
    } catch (error: any) {
      return rejectWithValue({
        message:
          error.message || 'Failed to fetch building tags with buildings',
      })
    }
  }
)

const initialState: BuildingTagsState = {
  tags: [],
  tagsWithBuildings: [],
  loading: false,
  error: null,
}

const buildingTagsSlice = createSlice({
  name: 'buildingTags',
  initialState,
  reducers: {
    clearBuildingTagsError: (state) => {
      state.error = null
    },
    clearBuildingTags: (state) => {
      state.tags = []
      state.tagsWithBuildings = []
    },
  },
  extraReducers: (builder) => {
    builder
      // Get all building tags
      .addCase(getAllBuildingTags.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(
        getAllBuildingTags.fulfilled,
        (state, action: PayloadAction<TagWithUserInfo[]>) => {
          state.loading = false
          state.tags = action.payload
        }
      )
      .addCase(getAllBuildingTags.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload?.message || 'Failed to fetch building tags'
      })

      // Create building tag
      .addCase(createBuildingTag.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(
        createBuildingTag.fulfilled,
        (state, action: PayloadAction<TagWithUserInfo>) => {
          state.loading = false
          state.tags.push(action.payload)
        }
      )
      .addCase(createBuildingTag.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload?.message || 'Failed to create building tag'
      })

      // Delete building tag
      .addCase(deleteBuildingTag.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(
        deleteBuildingTag.fulfilled,
        (state, action: PayloadAction<string>) => {
          state.loading = false
          state.tags = state.tags.filter((tag) => tag.id !== action.payload)
        }
      )
      .addCase(deleteBuildingTag.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload?.message || 'Failed to delete building tag'
      })

      // Get building tags with buildings
      .addCase(getBuildingTagsWithBuildings.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(
        getBuildingTagsWithBuildings.fulfilled,
        (state, action: PayloadAction<TagWithBuildings[]>) => {
          state.loading = false
          state.tagsWithBuildings = action.payload
        }
      )
      .addCase(getBuildingTagsWithBuildings.rejected, (state, action) => {
        state.loading = false
        state.error =
          action.payload?.message ||
          'Failed to fetch building tags with buildings'
      })
  },
})

export const { clearBuildingTagsError, clearBuildingTags } =
  buildingTagsSlice.actions

export const buildingTagsReducer = { buildingTags: buildingTagsSlice.reducer }
